import { createApi } from "@reduxjs/toolkit/query/react";
import * as time from "common/time";
import * as config from "config";
import * as FileSaver from "file-saver";
import * as utils from "utils";

const ASYNCH_READ_STATUS_DELAY = 1000;
const ASYNCHRONOUS_READ_TIMEOUT = 200000;

const makeUserFacingErrorFromString = (message) => {
  let err = "Unknown Error";
  if (message) {
    const m = message.match(
      /^.*Out of bounds.*timestamp: ([0-9]{4}-[0-9]{2}-[0-9]{2}).*$/i
    );
    if (m) err = "Illegal date (" + m[1] + ")";
  }
  return err;
};

const makeUserFacingError = (exception) => {
  if (exception?.status === 400 && exception?.description) {
    return String(exception.description);
  } else if (exception?.message) {
    return makeUserFacingErrorFromString(exception.message);
  }
  return "Unknown Error";
};

const baseQuery = async (args, api, extraOptions) => {
  const state = api.getState();
  const impersonatedCompanyId = state.impersonation?.companyId;

  try {
    const response = await utils.authenticatedFetch(
      config.endpoints.claims(args.url),
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: args.method ?? "post",
        body: args.body,
      },
      impersonatedCompanyId
    );
    return {
      data: await response.json(),
    };
  } catch (e) {
    return {
      error: makeUserFacingError(e),
    };
  }
};

const readAsynchInlineData = async (
  initialUrl,
  initialBody,
  statusUrl,
  api
) => {
  const startTime = new Date();
  const initial = await baseQuery(
    {
      url: initialUrl,
      body: initialBody,
    },
    api
  );
  if (initial.error) {
    return { error: makeUserFacingErrorFromString(initial.error) };
  }
  const sessionId = initial.data.sessionId;
  while (true) {
    const status = await baseQuery(
      {
        url: statusUrl,
        body: JSON.stringify({ sessionId }),
      },
      api
    );
    if (status.error) {
      return { error: makeUserFacingErrorFromString(status.error) };
    } else if (status.data?.status === "COMPLETE") {
      // Large results come back as {status, presignedUrl} pointing at the
      // stored (gzipped) claims instead of inline data.
      if (status.data.presignedUrl) {
        try {
          const response = await utils.plainFetch(status.data.presignedUrl);
          return { data: await response.json() };
        } catch (e) {
          return { error: makeUserFacingError(e) };
        }
      }
      return status.data;
    } else if (status.data?.status !== "PROCESSING") {
      return {
        error: makeUserFacingErrorFromString(status.data?.errorMessage),
      };
    }
    if (new Date() - startTime > ASYNCHRONOUS_READ_TIMEOUT) {
      return { error: "Timeout" };
    }
    await time.sleep(ASYNCH_READ_STATUS_DELAY);
  }
};

export const claimsApi = createApi({
  reducerPath: "claimsApi",
  baseQuery,
  endpoints: (builder) => ({
    claimsAggregate: builder.query({
      queryFn: async ({ claimsQuery }, api) => {
        return await readAsynchInlineData(
          "aggregated-claims-initiate",
          JSON.stringify(claimsQuery),
          "aggregated-claims-status",
          api
        );
      },
    }),
    claims: builder.query({
      queryFn: async ({ claimsQuery, limit, years, includeExcluded }, api) => {
        return await readAsynchInlineData(
          "download/json-initiate",
          JSON.stringify({
            query: claimsQuery,
            limit,
            years,
            includeExcluded,
          }),
          "download/json-status",
          api
        );
      },
    }),
    summaryChart: builder.query({
      queryFn: async (claimsQuery, api) => {
        return await readAsynchInlineData(
          "charts/summary-initiate",
          JSON.stringify(claimsQuery),
          "charts/summary-status",
          api
        );
      },
    }),
    aggregateLossByLineCharts: builder.query({
      queryFn: async (claimsQuery, api) => {
        return await readAsynchInlineData(
          "charts/aggregate-loss-by-line-charts-initiate",
          JSON.stringify(claimsQuery),
          "charts/aggregate-loss-by-line-charts-status",
          api
        );
      },
    }),
    allClaimsChart: builder.query({
      queryFn: async ({ claimsQuery, claimsAbove }, api) => {
        const startTime = new Date();
        try {
          const initial = await baseQuery(
            {
              url: "charts/all-claims-initiate",
              body: JSON.stringify({ ...claimsQuery, claimsAbove }),
            },
            api
          );
          while (true) {
            const status = await baseQuery(
              {
                url: "charts/all-claims-status",
                body: JSON.stringify({ sessionId: initial.data.sessionId }),
              },
              api
            );
            if (status.data?.status === "COMPLETE") {
              const response = await utils.plainFetch(status.data.url);
              const data = await response.json();
              return {
                data: data,
              };
            }
            if (status.data?.status === "ERROR") {
              return {
                error: makeUserFacingErrorFromString(status.data.errorMessage),
              };
            }
            if (new Date() - startTime > ASYNCHRONOUS_READ_TIMEOUT) {
              return { error: "Timeout" };
            }
            await time.sleep(ASYNCH_READ_STATUS_DELAY);
          }
        } catch (e) {
          return { error: makeUserFacingError(e) };
        }
      },
    }),
    claimsComparison: builder.query({
      queryFn: async ({
        currentQuery,
        priorQuery,
        primaryMatch,
        requiredMatchColumns,
        matchColumns,
        textDeltaColumns,
        numericDeltaColumns,
        page = 0,
        limit = 100,
        pollingIntervalSeconds = 1,
      }) => {
        try {
          let response = await utils.authenticatedFetch(
            config.endpoints.claims("charts/trigger-claims-comparison"),
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                currentQuery,
                priorQuery,
                primaryMatch,
                requiredMatchColumns,
                matchColumns,
                textDeltaColumns,
                numericDeltaColumns,
                page,
                limit,
              }),
            }
          );
          const creationResponse = await response.json();
          const reportId = creationResponse.id;

          const delay = (seconds) =>
            new Promise((res) => setTimeout(res, 1000 * seconds));

          let status = null;
          while (
            status == null ||
            ["pending", "running"].includes(status.state)
          ) {
            await delay(pollingIntervalSeconds);
            response = await utils.authenticatedFetch(
              config.endpoints.claims("charts/retrieve-claims-comparison"),
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({ id: reportId }),
              }
            );
            status = await response.json();
          }
          if (status.state !== "succeeded") {
            return { error: makeUserFacingError(status.error) };
          }
          response = await fetch(status.url);
          const data = await response.json();
          return { data };
        } catch (e) {
          return makeUserFacingError(e);
        }
      },
    }),
    recommendedBaseLayer: builder.query({
      query: (claimsQuery) => ({
        url: "pricing/recommended-layer",
        body: JSON.stringify(claimsQuery),
      }),
    }),
    downloadExcelClaims: builder.mutation({
      queryFn: async ({
        claimsQuery,
        filename,
        pollingIntervalSeconds = 1,
      }) => {
        try {
          let response = await utils.authenticatedFetch(
            config.endpoints.claims("create-claims-excel"),
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(claimsQuery),
            }
          );
          const creationResponse = await response.json();
          const id = creationResponse.id;

          const delay = (seconds) =>
            new Promise((res) => setTimeout(res, 1000 * seconds));

          let status = null;
          while (
            status == null ||
            ["pending", "running"].includes(status.state)
          ) {
            await delay(pollingIntervalSeconds);
            response = await utils.authenticatedFetch(
              config.endpoints.claims("retrieve-claims-excel"),
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({ id }),
              }
            );
            status = await response.json();
          }
          if (status.state !== "succeeded") {
            return { error: makeUserFacingErrorFromString(status.error) };
          }
          response = await fetch(status.downloadUrl);
          const blob = await response.blob();
          FileSaver.saveAs(blob, filename);
          return { data: status };
        } catch (e) {
          return makeUserFacingError(e);
        }
      },
    }),
    exposures: builder.query({
      query: () => ({
        url: "exposure/types",
        method: "get",
      }),
    }),
    defaultMeasuresByIndustryClass: builder.query({
      query: () => ({
        url: "exposure/default-measures-by-industry-class",
        method: "get",
      }),
    }),
    industryClasses: builder.query({
      query: () => ({
        url: "exposure/industry-classes",
        method: "get",
      }),
    }),
    NAICSCodes: builder.query({
      // Encoded, matching the search selector's URLSearchParams. Titles now reach
      // here from agent search, so an unescaped "&" would truncate the query
      // and make this disagree with the selector on the same input.
      query: ({ query }) => ({
        url: `pricing/naics/query?q=${encodeURIComponent(query)}`,
        method: "get",
      }),
    }),
    GICSCategoryCodes: builder.query({
      query: ({ query }) => ({
        url: `pricing/classifications/query?q=${encodeURIComponent(query)}`,
        method: "get",
      }),
    }),
  }),
});

export const {
  useAggregateLossByLineChartsQuery,
  useClaimsComparisonQuery,
  useRecommendedBaseLayerQuery,
  useDownloadExcelClaimsMutation,
  useIndustryClassesQuery,
  useNAICSCodesQuery,
  useGICSCategoryCodesQuery,
  useExposuresQuery,
  useDefaultMeasuresByIndustryClassQuery,
  useClaimsAggregateQuery,
  useClaimsQuery,
  useAllClaimsChartQuery,
  useSummaryChartQuery,
} = claimsApi;
